import {Component, HostListener, inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {AdminLocationPlaceService} from '../admin-location-place.service';
import {MetaField} from '../../../core/definitions/meta-field';
import {SectionsContainer} from '../../../core/definitions/sections-container';
import {CreateSectionsContainerParams, ObjectEditService} from '../../../core/object-edit.service';
import {UiToolsService} from '../../../core/ui-tools.service';
import {TranslateService} from '@ngx-translate/core';
import {System} from "../../../core/definitions/system";
import {LoggerService} from "../../../core/logger.service";
import {ObjectTypes} from "../../../core/definitions/object-types";

export interface AdminLocationFormModalData {
  id: string;
  objectType: string;
  parentId?: string;
  headingCreateNew: string;
  headingEditExisting: string;
  currentLevelName?: string;
}

@Component({
    selector: 'app-admin-location-place-form-modal',
    templateUrl: './admin-location-place-form-modal.component.html',
    styleUrls: ['./admin-location-place-form-modal.component.scss'],
    standalone: false
})
export class AdminLocationPlaceFormModalComponent implements OnInit {
  // Injections
  readonly data: AdminLocationFormModalData = inject(MAT_DIALOG_DATA);
  readonly dialogRef: MatDialogRef<AdminLocationPlaceFormModalComponent> = inject(MatDialogRef);
  private readonly locationPlaceService: AdminLocationPlaceService = inject(AdminLocationPlaceService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly objectEditService: ObjectEditService = inject(ObjectEditService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly uiTools: UiToolsService = inject(UiToolsService);

  sectionsContainer: SectionsContainer;
  primeFields: Array<MetaField>;
  model: any;
  parentName: string;
  private oldInvalidFieldLength = 0;
  private invalidFieldText = '';

  /**
   * Number of objects this location has been referenced from, if any.
   */
  objectCount: number;
  /**
   * A concatenated string of the names for all the
   * objects referencing this location, if any,
   */
  locationUsedIn: string;
  storeStatus = {
    failed: false,
    message: '',
    serverMsg: ''
  };

  @HostListener('click', ['$event.target'])
  registerClick(event: any) {
    this.uiTools.registerDocumentClick(event);
  }

  ngOnInit() {
    this.init().then();
  }

  private async init() {
    if (this.data?.id) {
      this.model = await this.locationPlaceService.getLocation(this.data.id);
      this.sectionsContainer = await this.createObjectSectionsContainer(this.model);
    } else if (this.data?.objectType === ObjectTypes.PLACE.toString()) {
      this.model = await this.locationPlaceService.getDefaultPlaceModel(
        this.data.objectType, this.data.parentId, this.data.currentLevelName);
      this.sectionsContainer = await this.createObjectSectionsContainer(this.model);
    } else {
      this.model = await this.locationPlaceService.getDefaultLocationModel(
        this.data.objectType, this.data.parentId, this.data.currentLevelName);
      this.sectionsContainer = await this.createObjectSectionsContainer();
    }
    if (this.data?.currentLevelName) {
      this.parentName = this.data.currentLevelName;
    }
    if (this.model?.artifact_id && this.data.objectType === 'Location') {
      const res = await this.locationPlaceService.getObjectNamesWhereLocationIsUsed(this.model.artifact_id);
      this.objectCount = res.numObjects;
      this.locationUsedIn = res.objects?.map(o => o.name).join(', ');
    }
  }

  private async createObjectSectionsContainer(object?: any): Promise<SectionsContainer> {
    const useExisting = !!object;
    let sectionsContainer: SectionsContainer;
    if (object?.artifact_id) {
      sectionsContainer = await this.objectEditService.loadObjectGetSectionsContainer(object.artifact_id, true);
      sectionsContainer.isDialog = true;
      return sectionsContainer;
    } else {
      const params = {
        useExistingObject: useExisting,
        usePrimeFields: this.data.objectType === 'Location' ? !useExisting : false,
        objectType: this.data.objectType,
        objectId: object ? object.artifact_id : null,
        object: object || this.model,
        getSourceObject: false
      } as CreateSectionsContainerParams;

      sectionsContainer = await this.objectEditService.createSectionsContainer(params);
      sectionsContainer.isDialog = true;

      if (params.usePrimeFields) {
        this.primeFields = sectionsContainer.primeFields;
      }

      return sectionsContainer;
    }
  }

  get disabled() {
    return !this.sectionsContainer ||
      this.sectionsContainer.formGroup.invalid ||
      !this.sectionsContainer.formGroup.dirty;
  }

  get disabledReason() {
    let text = '';
    if (this.disabled) {
      if ( this.sectionsContainer.rootObject.has_authority) {
        text = this.translate.instant('TRANS__DISABLE_REASON__HAS_AUTHORITY');
      } else if (this.sectionsContainer?.formGroup.invalid) {
        const invalidFields = this.getInvalidFields();
        if (invalidFields.length !== this.oldInvalidFieldLength) {
          this.oldInvalidFieldLength = invalidFields.length;
          this.invalidFieldText = this.generateDisabledReason(invalidFields);
        }
        text = this.invalidFieldText;
      } else {
        text = this.translate.instant('TRANS__DISABLE_REASON__NO_CHANGES');
      }
    } else {
      this.oldInvalidFieldLength = 0;
    }
    return text;
  }

  private getInvalidFields() {
    const res = [];
    for (const name in this.sectionsContainer.formGroup.controls) {
      if (this.sectionsContainer.formGroup.controls[name].invalid) {
        res.push(name);
      }
    }
    return res;
  }

  private generateDisabledReason(invalidFields: string[]) {
    let text = this.translate.instant('TRANS__VALIDATION__INVALID_FIELDS');
    text += ': ';
    let add = '';
    for (const name of invalidFields) {
      const firstName = name.split(System.keySep)[0];
      const fieldMeta = this.sectionsContainer.rootObject.$$meta[firstName];
      if (fieldMeta) {
        const fieldTitle = fieldMeta.title || fieldMeta.admin_title;
        text += add + this.translate.instant(fieldTitle);
        add = ', ';
      } else {
        this.logger.info(`No metadata found for field ${firstName}`);
      }
    }
    return text;
  }

  // handleAbortClicked(): void {
  //   this.dialogRef.close(false);
  // }

  handleSaveClicked(): void {
    this.objectEditService.setObjectValuesStoreObject(this.sectionsContainer, true).then(
      value => {
        this.dialogRef.close(value);
      },
      error_ => {
        this.storeStatus.failed = true;
        this.storeStatus.message = 'TRANS__EDIT_OBJECT_DIALOG__STORE_FAILED';
        this.storeStatus.serverMsg = error_.error?.message || error_.errorMessage;
        if (this.storeStatus.serverMsg.includes('not unique')) {
          this.storeStatus.serverMsg = 'TRANS__EDIT_OBJECT_DIALOG__STORE_FAILED_NOT_UNIQUE';
        }
      }
    );
  }

  // private async handleFormValueChanged(val: Partial<Location>): Promise<void> {
  //   if (val && val.hasOwnProperty('valid')) {
  //     this.validValueAfterEdit = val.valid;
  //   }
  //   // this.cdr.markForCheck();
  // }
}
